import React from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button, Typography, Tag, Divider, Alert } from "antd";
import { PlayCircleOutlined } from "@ant-design/icons";
import { imagenDeRespaldo } from "../imagenDeRespaldo";
import { TextoClinico } from "../../../uiComponents/TextoClinico";
import { confirmar } from "../../../../ui/confirmar";
import "./SceneDetailModal.scss";

const { Title, Paragraph } = Typography;

export function SceneDetailModal({ isOpen, onClose, scene, onLaunch }) {
  // "Cerrar" ya vive en el bloque comun. El hook va ANTES del early return.
  const { t } = useTranslation(["rooms", "common"]);
  // El texto va pintado dentro del SVG, asi que se construye con el idioma
  // activo en vez de ser una constante de modulo.
  const FALLBACK_IMG = imagenDeRespaldo(t("detalleSala.sinImagen"), {
    ancho: 720,
    alto: 240,
  });

  if (!scene) return null;

  const { module_01, module_07 } = scene;

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose} style={{ borderRadius: "8px" }}>
          {t("common:actions.close")}
        </Button>,
        // Lanzar no consulta: crea una sala VR abierta tres horas y abre otra
        // pestana. La confirmacion lo dice antes de hacerlo. `onLaunch` abre la
        // pestana de forma sincrona dentro del gesto de aceptar, asi que el
        // bloqueador de ventanas emergentes no salta.
        <Button
          key="launch"
          type="primary"
          icon={<PlayCircleOutlined />}
          onClick={() =>
            confirmar({
              titulo: t("detalleSala.confirmarLanzarTitulo"),
              consecuencia: t("detalleSala.confirmarLanzarCuerpo"),
              textoOk: t("detalleSala.lanzar"),
              peligroso: false,
              onOk: () => onLaunch(scene),
            })
          }
          style={{ borderRadius: "8px", fontWeight: 600 }}
        >
          {t("detalleSala.lanzar")}
        </Button>,
      ]}
      width={720}
      centered
      className="revi-scene-modal"
    >
      <div className="scene-detail-content">
        {/* Banner */}
        <div className="detail-banner">
          <img
            src={module_01?.bannertop?.url || FALLBACK_IMG}
            alt={module_01?.ModOneTitle || t("detalleSala.altEscena")}
            onError={(e) => {
              e.currentTarget.src = FALLBACK_IMG;
            }}
          />
        </div>

        {/* Header Info */}
        <div className="detail-header">
          <Title level={3} style={{ marginBottom: 12, marginTop: 0 }}>
            {module_01?.ModOneTitle}
          </Title>

          <div className="detail-tags">
            {module_07?.ModSevenTherapyIds?.map((tag, idx) => {
              const key =
                tag?._id ??
                tag?.id ??
                tag?.title ??
                tag?.name ??
                (typeof tag === "string" ? tag : null) ??
                `tag-${idx}`;

              const label =
                tag?.title ??
                tag?.name ??
                (typeof tag === "string"
                  ? tag
                  : t("detalleSala.etiquetaSinNombre"));

              return (
                <Tag
                  key={key}
                  color={tag?.colorCode}
                  style={{
                    padding: "4px 10px",
                    borderRadius: "6px",
                    border: "none",
                  }}
                >
                  {label}
                </Tag>
              );
            })}

            {module_07?.ModSevenDifficultyLevel && (
              <Tag
                variant="filled" // ✅ en vez de bordered={false}
                style={{ background: "#F3F4F6", color: "#4B5563" }}
              >
                {t("detalleSala.nivel", {
                  nivel: module_07.ModSevenDifficultyLevel,
                })}
              </Tag>
            )}
          </div>
        </div>

        <Divider style={{ margin: "20px 0" }} />

        {/* Descripción. Los tres textos clinicos pasan por TextoClinico:
            parrafos, saltos de linea, listas y negritas tal y como los escribio
            quien dio de alta la sala. Antes salian "todo seguido". */}
        <div className="detail-section">
          <Title level={5} style={{ fontSize: 16 }}>
            {t("detalleSala.descripcionClinica")}
          </Title>
          <TextoClinico
            valor={module_07?.ModSevenClinicalDescription}
            className="detail-text"
            vacio={
              <Paragraph style={{ color: "#4B5563", lineHeight: 1.6 }}>
                {t("detalleSala.noDisponible")}
              </Paragraph>
            }
          />
        </div>

        {/* Guía (HTML del auto-tagger o texto plano del admin) */}
        {module_07?.ModSevenInterventionGuide && (
          <div className="detail-section">
            <Title level={5} style={{ fontSize: 16 }}>
              {t("detalleSala.guiaIntervencion")}
            </Title>
            <TextoClinico
              valor={module_07.ModSevenInterventionGuide}
              className="html-content"
            />
          </div>
        )}

        {/* Alertas */}
        {module_07?.ModSevenContraindications && (
          <div className="detail-section" style={{ marginTop: 24 }}>
            <Alert
              title={t("detalleSala.precauciones")} // ✅ en vez de message
              description={
                <TextoClinico valor={module_07.ModSevenContraindications} />
              }
              type="warning"
              showIcon
              style={{ borderRadius: "8px" }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
